// ManagerSettingDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { tokens } from "../../theme";
import {
  AccountCircle,
  EmailRounded,
  Lock,
  Password,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import ConfirmDialog from "../common/ComfirmationDialog";

const ManagerSettingDialog = ({
  open,
  onClose,
  manager = {},
  compoundId,
  onSave,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [formData, setFormData] = useState({
    username: manager?.username || "",
    email: manager?.email || "",
    phone: manager?.phone || "",
    password: "",
    confirm: "",
  });
  useEffect(() => {
    setFormData({
      username: manager?.username || "",
      email: manager?.email || "",
      phone: manager?.phone || "",
      password: "",
      confirm: "",
    });
  }, [manager, open]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirm) {
      setSnackbar({
        open: true,
        message: "Passwords do not match",
        severity: "error",
      });
      return;
    }
    setIsSubmit(true);
    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        phone: formData.phone,
        compoundId,
      };

      if (formData.password) {
        payload.password = formData.password;
        payload.confirm = formData.confirm;
      }
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/manager/save`,
        payload,
        { withCredentials: true }
      );
      setSnackbar({
        open: true,
        message: "Manager saved",
        severity: "success",
      });
      onSave?.();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Error saving manager";
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsSubmit(false);
    }
  };

  const handleDelete = () => {
    setConfirmOpen(true); // open dialog
  };
  const confirmDelete = async () => {
    if (!manager.id) return;
    setConfirmOpen(false); // close dialog
    setIsSubmit(true);
    setSnackbar({
      open: true,
      message: "Deleting manager...",
      severity: "info",
    });
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/manager/delete/${manager.id}`,
        { withCredentials: true }
      );
      setSnackbar({
        open: true,
        message: "Manager deleted",
        severity: "success",
      });
      onSave?.();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Error deleting manager";

      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          Manager Settings
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={2}>
            <TextField
              label="Username"
              name="username"
              fullWidth
              value={formData.username}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone"
              name="phone"
              fullWidth
              value={formData.phone}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <EmailRounded />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              fullWidth
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" mr={2}>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#90a4ae" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              name="confirm"
              fullWidth
              value={formData.confirm}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" mr={2}>
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmit}
            disabled={isSubmit}
            variant="contained"
          >
            {isSubmit ? "Saving..." : "Save"}
          </Button>
          <Button
            onClick={handleDelete}
            sx={{
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.light",
                color: "white",
              },
            }}
          >
            Delete
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Manager?"
        content="Are you sure you want to delete this manager? This action cannot be undone."
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default ManagerSettingDialog;
